import ProductCounter from '@blocks/product/product-counter'
import ProductPrice from '@blocks/product/product-price'
import Photo from '@components/photo'
import SimpleLink from '@components/simple-link'
import { isMarketingConsentGranted } from '@lib/analytics/consent'
import { AnalyticsContext } from '@lib/analytics/context'
import { getProductEventPayload } from '@lib/analytics/events'
import { AnalyticsEventName } from '@lib/analytics/types'
import { CartContext } from '@lib/cart/context'
import { LineItem } from '@lib/cart/types'
import { getPriceNumber } from '@lib/helpers'
import { LanguageContext } from '@lib/language-context'
import { getFormattedProductTitle } from '@lib/product'
import { getProductListingThumbnails } from '@lib/product-images'
import { getLinkPageUrl, getPageUrl } from '@lib/routes'
import { ShopContext } from '@lib/shop-context'
import { SiteContext } from '@lib/site-context'
import { StringsContext } from '@lib/strings-context'
import cx from 'classnames'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

interface CartItemProps {
  item: LineItem
  index: number
  className?: string
}

const CartItem = ({ item, index, className }: CartItemProps) => {
  const { triggerAnalyticsEvent } = useContext(AnalyticsContext)
  const { toggleCart, updateCartItem, removeItemFromCart } =
    useContext(CartContext)
  const { locale } = useContext(LanguageContext)
  const { currencyCode } = useContext(ShopContext)
  const { settings } = useContext(SiteContext)
  const strings = useContext(StringsContext)

  const [errorMessage, setErrorMessage] = useState('')

  const quantity = useRef(item.quantity ?? 1)

  useEffect(() => {
    quantity.current = item.quantity ?? 1
  }, [item.quantity])

  const thumbnailImage = useMemo(() => {
    const listingPhotos = !!item.product.inheritCartPhotos
      ? item.product.listingPhotos
      : item.product.cartPhotos

    return getProductListingThumbnails(
      item.product.galleryPhotos ?? [],
      listingPhotos ?? [],
      item.options,
    )
  }, [item.product, item.options])

  const productUrl = useMemo(() => {
    if (!item.product.slug.current) {
      return null
    }

    return getLinkPageUrl('product', item.product.slug.current, {
      variant: `${item.variantID}`,
    })
  }, [item])

  const attributes = useMemo(() => {
    const attributes = item.customAttributes ?? []

    return attributes.filter((attribute) => {
      return !attribute.key.startsWith('_')
    })
  }, [item])

  const handleUpdate = useCallback(
    async (newQuantity: number, oldQuantity: number) => {
      try {
        setErrorMessage('')

        const updateResult = await updateCartItem(item.lineId, newQuantity)

        // Check if quantity update was not successful
        if (updateResult.userErrors || updateResult.error) {
          // Revert quantity
          quantity.current = oldQuantity
          await handleUpdate(oldQuantity, oldQuantity)

          // Show error message
          const userError = updateResult.userErrors?.find(
            (userError) => userError.field?.[1] === `${index}`,
          )

          if (userError) {
            setErrorMessage(userError.message)
          }

          throw new Error(
            updateResult.error ?? 'Failed to update item quantity',
          )
        }

        const eventPayload = getProductEventPayload(
          getFormattedProductTitle(locale, item.product.title),
          [
            {
              variantId: item.variantID,
              options: item.options,
              price: item.price,
              comparePrice: item.comparePrice,
              quantity: Math.abs(newQuantity - oldQuantity),
            },
          ],
          currencyCode,
        )

        if (newQuantity > oldQuantity) {
          // Trigger add to cart event
          triggerAnalyticsEvent(AnalyticsEventName.AddToCart, eventPayload)

          // Klaviyo event
          if (settings && window.klaviyo && isMarketingConsentGranted()) {
            const domain = settings.siteURL.replace(/\/+$/g, '')
            const productUrl = getPageUrl(
              locale,
              'product',
              item.product.slug.current,
              {
                variant: `${item.variantID}`,
              },
            )
            const klaviyoProductItem: KlaviyoProductItem = {
              ProductName: getFormattedProductTitle(locale, item.product.title),
              ProductID: `${item.product.productID}`,
              VariantID: `${item.variantID}`,
              URL: `${domain}${productUrl}`,
              Price: getPriceNumber(item.price),
            }

            if (item.comparePrice) {
              klaviyoProductItem.CompareAtPrice = getPriceNumber(
                item.comparePrice,
              )
            }

            window.klaviyo.track('Added To Cart', klaviyoProductItem)
          }

          // Triple Whale pixel event
          if (window.TriplePixel) {
            window.TriplePixel('AddToCart', {
              item: `${item.product.productID}`,
              q: Math.abs(newQuantity - oldQuantity),
              v: `${item.variantID}`,
            })
          }
        }

        if (newQuantity < oldQuantity) {
          // Trigger remove from cart event
          triggerAnalyticsEvent(AnalyticsEventName.RemoveFromCart, eventPayload)
        }
      } catch (error) {
        console.error(error)
      }
    },
    [
      currencyCode,
      index,
      item,
      locale,
      settings,
      triggerAnalyticsEvent,
      updateCartItem,
    ],
  )

  const handleClose = useCallback(() => {
    toggleCart(false)
  }, [toggleCart])

  const handleRemove = useCallback(() => {
    removeItemFromCart(item.lineId)

    // Trigger remove from cart event
    triggerAnalyticsEvent(
      AnalyticsEventName.RemoveFromCart,
      getProductEventPayload(
        getFormattedProductTitle(locale, item.product.title),
        [
          {
            variantId: item.variantID,
            options: item.options,
            price: item.price,
            comparePrice: item.comparePrice,
            quantity: item.quantity,
          },
        ],
        currencyCode,
      ),
    )
  }, [currencyCode, item, locale, removeItemFromCart, triggerAnalyticsEvent])

  return (
    <div className={cx('grid grid-cols-[auto,1fr] gap-3', className)}>
      {!!thumbnailImage && (
        <Photo
          image={thumbnailImage}
          sizes="(min-width: 768px) 400px, 35vw"
          className="max-w-20 xs:max-w-24"
        />
      )}

      <div className="flex flex-col gap-2 pb-3">
        <div className="grid grid-cols-2 gap-x-3">
          <div>
            <h3 className="text-xs leading-normal">
              {!!productUrl && (
                <SimpleLink
                  href={productUrl}
                  onClick={handleClose}
                  onBeforeInput={handleClose}
                  tabIndex={0}
                >
                  {getFormattedProductTitle(locale, item.product.title)}
                </SimpleLink>
              )}
              {!productUrl && (
                <>{getFormattedProductTitle(locale, item.product.title)}</>
              )}
            </h3>

            {item.options.map((option) => (
              <p key={option.name} className="text-xs uppercase">
                {option.name}: {option.value}
              </p>
            ))}
          </div>

          <ProductPrice
            price={item.price}
            className="text-xs leading-normal self-start justify-self-end"
          />

          <div className="col-span-2">
            {attributes.map((attribute, index) => (
              <p key={index} className="text-xs uppercase text-gray-dark">
                {attribute.key}: {attribute.value}
              </p>
            ))}
          </div>

          <ProductCounter
            id={`${item.variantID}`}
            quantity={quantity}
            onUpdate={handleUpdate}
            className="mt-6 justify-self-start"
          />

          <button
            onClick={handleRemove}
            className={cx(
              'justify-self-end self-center mt-6',
              'text-xs text-gray-dark uppercase underline hover:no-underline',
            )}
          >
            {strings.buttonRemove}
          </button>
        </div>

        {!!errorMessage && (
          <div>
            <span className="text-xs text-error">{errorMessage}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default CartItem
